import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { Form, Checkbox } from "antd";

import { useStoreOperations } from "../../hooks/inventario/useStoreOperations";
import { CustomModal } from "../../components/Modals/CustomModal";

import "./InventarioAssignStores.css";

/**
 * Permite seleccionar múltiples almacenes para un producto.
 * Los checkboxes se preseleccionan según los almacenes actuales del producto
 *
 * @param {boolean} visible - Control de visibilidad
 * @param {function} onClose - Callback al cerrar
 * @param {function} onSubmit - Callback al guardar
 * @param {Object} producto - Información del producto (opcional)
 */
export const InventarioAssignStores = ({
  visible,
  onClose,
  onSubmit,
  producto = null,
  saving = false,
}) => {
  const [form] = Form.useForm();
  const prevVisibleRef = useRef(visible);
  const initialStoreIdsRef = useRef([]);

  const {
    computed: { activeStores },
  } = useStoreOperations();

  //*  SINCRONIZAR VALORES INICIALES
  useEffect(() => {
    const wasVisible = prevVisibleRef.current;

    // Transicion de abierto -> cerrado: limpiar solo una vez
    if (wasVisible && !visible) {
      form.resetFields();
      initialStoreIdsRef.current = [];
    }

    // Transici�n de cerrado -> abierto con producto: setear iniciales
    if (!wasVisible && visible && producto) {
      const initialsStoreIds =
        producto.almacenes?.filter((store) => store.active).map((store) => store.id) || [];

      initialStoreIdsRef.current = initialsStoreIds;

      form.setFieldsValue({
        almacenes: initialsStoreIds,
      });
    }

    prevVisibleRef.current = visible;
  }, [visible, producto, form]);

  //*  HANDLERS
  /**
   * Maneja el click en Guardar
   */
  const handleOk = () => {
    if (saving) return;
    form.submit();
  };

  /**
   * Maneja el cierre/cancelación del modal
   */
  const handleCancel = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  /**
   * Procesa el envío del formulario
   * @param {Object} values - Valores del formulario { almacenes: [...] }
   */
  const onFinish = async (values) => {
    const selectedIds = (values.almacenes || []).map((id) => {
      return typeof id === "string" ? Number.parseInt(id, 10) : id;
    });

    const initialStoreIds = initialStoreIdsRef.current;
    const sameLength = selectedIds.length === initialStoreIds.length;
    const sameValues = sameLength && selectedIds.every((id) => initialStoreIds.includes(id));

    if (sameValues) {
      form.setFields([
        {
          name: "almacenes",
          errors: ["Debe seleccionar al menos un almacén adicional diferente al inicial."],
        },
      ]);
      return;
    }

    const result = await onSubmit(selectedIds);

    if (!result || !result.success) {
      form.setFields([
        {
          name: "almacenes",
          errors: [result?.error || "Error al asignar almacenes. Intente nuevamente."],
        },
      ]);
      return;
    }

    form.resetFields();
    onClose();
  };

  return (
    <CustomModal
      visible={visible}
      title="Agregar a almacén"
      onClose={handleCancel}
      onOk={handleOk}
      okText={saving ? "Guardando..." : "Guardar"}
      cancelText="Cancelar"
      okButtonProps={{
        disabled: saving,
        loading: saving,
      }}
      cancelButtonProps={{
        disabled: saving,
      }}
      width="380px"
      compact={true}
    >
      <div className="assign-stores-container">
        <p className="assign-stores-description">
          Seleccione los almacenes a los que se agregará el ítem
        </p>

        <Form form={form} layout="vertical" onFinish={onFinish} className="assign-stores-form">
          <Form.Item
            name="almacenes"
            className="assign-stores-form-item"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error("Debe seleccionar al menos un almacén."));
                  }
                },
              },
            ]}
          >
            <Checkbox.Group className="assign-stores-checkboxes">
              {activeStores.map((almacen) => (
                <div key={almacen.id} className="assign-stores-checkbox-wrapper">
                  <span className="assign-stores-checkbox-label">{almacen.name}</span>
                  <Checkbox value={almacen.id} />
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </div>
    </CustomModal>
  );
};

InventarioAssignStores.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  producto: PropTypes.object,
  saving: PropTypes.bool,
};
